import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Icon } from "@iconify/react";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { mobileCpoOnboardingHandoffEmailUrl, mobileCpoOnboardingSectionUpdateUrl, mobileCpoOnboardingStatusUpdateUrl } from "src/react-query/endPoints";
import api from "src/util/axios";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { debounceFunc } from "src/utils/helper";


const TIMELINE_STATUS_LABELS = {
  NEW_APPLICATION:                    "New Application",
  UNDER_REVIEW:                       "Under Review",
  INFO_REQUIRED:                      "Info Required",
  INFO_SUBMITTED:                     "Info Submitted",
  REJECTED:                           "Rejected",
  ACCEPTED:                           "Accepted",
  RESOURCES_PROVISIONING_IN_PROGRESS: "Provisioning In Progress",
  RESOURCES_PROVISIONED:              "Provisioned",
  RESOURCES_PROVISIONING_FAILED:      "Provisioning Failed",
  ADD_FIRST_CHARGER:                  "Add First Charger",
  COMPLETE_CPO_APPLICATION:           "Complete CPO Application",
  APPLICATION_SUBMITTED:              "Application Submitted",
  VERIFICATION_AND_ACTIVATION:        "Verification & Activation",
  DRIVER_PAYMENT_QR_READY:            "Driver Payment QR Ready",
};

const TIMELINE_STATUS_COLORS = {
  NEW_APPLICATION:                    { color: "default",   dotColor: "#9e9e9e" },
  UNDER_REVIEW:                       { color: "primary",   dotColor: "#1976d2" },
  INFO_REQUIRED:                      { color: "warning",   dotColor: "#ed6c02" },
  INFO_SUBMITTED:                     { color: "info",      dotColor: "#0288d1" },
  ACCEPTED:                           { color: "success",   dotColor: "#2e7d32" },
  REJECTED:                           { color: "error",     dotColor: "#d32f2f" },
  RESOURCES_PROVISIONING_IN_PROGRESS: { color: "secondary", dotColor: "#7b1fa2" },
  RESOURCES_PROVISIONED:              { color: "success",   dotColor: "#1b5e20" },
  RESOURCES_PROVISIONING_FAILED:      { color: "error",     dotColor: "#c62828" },
  ADD_FIRST_CHARGER:                  { color: "info",      dotColor: "#0277bd" },
  COMPLETE_CPO_APPLICATION:           { color: "primary",   dotColor: "#1565c0" },
  APPLICATION_SUBMITTED:              { color: "info",      dotColor: "#01579b" },
  VERIFICATION_AND_ACTIVATION:        { color: "secondary", dotColor: "#6a1b9a" },
  DRIVER_PAYMENT_QR_READY:            { color: "success",   dotColor: "#00695c" },
};

const SECTION_TO_COLUMN = {
  charger:   "charger_requested",
  profile:   "business_details",
  gstin:     "gstin_details",
  kyc:       "kyc_details",
  agreement: "agreement_details",
  invoice:   "invoice_details",
  bank:      "bank_details",
};

const REVIEW_SECTIONS = [
  {
    key: "charger", title: "Charger",
    getStatus: (app) => app?.charger_requested?.status ?? null,
    getNote:   (app) => app?.charger_requested?.note ?? null,
    getFields: (app) => [
      { label: "Address", value: app?.charger_requested?.address?.address_line1 },
      { label: "City",    value: app?.charger_requested?.address?.city },
      { label: "State",   value: app?.charger_requested?.address?.state },
      { label: "Country", value: app?.charger_requested?.address?.country },
      { label: "Pincode", value: app?.charger_requested?.address?.zip_code },
    ],
    getDocs:   () => [],
  },
  {
    key: "profile", title: "Business detail",
    getStatus: (app) => app?.business_details?.status ?? null,
    getNote:   (app) => app?.business_details?.note ?? null,
    getFields: (app) => [
      { label: "Trade name",         value: app?.business_details?.trade_name },
      { label: "Business name",      value: app?.business_details?.business_name },
      { label: "Business type",      value: app?.business_details?.business_type },
      { label: "Registered address", value: app?.business_details?.registered_address },
      { label: "City",               value: app?.business_details?.business_city },
      { label: "State",              value: app?.business_details?.business_state },
      { label: "Pincode",            value: app?.business_details?.business_zipcode },
    ],
    getDocs:   () => [],
  },
  {
    key: "kyc", title: "KYC",
    getStatus: (app) => app?.kyc_details?.status ?? null,
    getNote:   (app) => app?.kyc_details?.note ?? null,
    getFields: (app) => [
      { label: "PAN",            value: app?.kyc_details?.pan },
      { label: "Aadhaar number", value: app?.kyc_details?.aadhar_number },
    ],
    getDocs: (app) => [
      { label: "PAN Document",     url: app?.kyc_details?.pan_copy_url },
      { label: "Aadhaar Document", url: app?.kyc_details?.aadhar_scanned_url },
    ].filter((d) => d.url),
  },
  {
    key: "gstin", title: "GSTIN",
    getStatus: (app) => app?.gstin_details?.status ?? null,
    getNote:   (app) => app?.gstin_details?.note ?? null,
    getFields: (app) => [
      { label: "GSTIN", value: app?.gstin_details?.gstin },
      { label: "TAN",   value: app?.gstin_details?.tan },
    ],
    getDocs: () => [],
  },
  {
    key: "bank", title: "Bank",
    getStatus: (app) => app?.bank_details?.status ?? null,
    getNote:   (app) => app?.bank_details?.note ?? null,
    getFields: (app) => [
      { label: "Beneficiary name", value: app?.bank_details?.beneficiary_name },
      { label: "Account no",       value: app?.bank_details?.account_no },
      { label: "Account type",     value: app?.bank_details?.account_type },
      { label: "IFSC code",        value: app?.bank_details?.ifsc_code },
    ],
    getDocs: (app) => [
      { label: "Cancelled Cheque", url: app?.bank_details?.cancelled_cheque_url },
    ].filter((d) => d.url),
  },
  {
    key: "invoice", title: "Invoice",
    getStatus: (app) => app?.invoice_details?.status ?? null,
    getNote:   (app) => app?.invoice_details?.note ?? null,
    getFields: (app) => [
      { label: "Invoice no",   value: app?.invoice_details?.invoice_number },
      { label: "Invoice date", value: app?.invoice_details?.invoice_date },
      {
        label: "Amount",
        value: app?.invoice_details?.invoice_amount != null
          ? `₹ ${Number(app.invoice_details.invoice_amount).toLocaleString("en-IN")}`
          : null,
      },
    ],
    getDocs: (app) => [
      { label: "Scanned Invoice", url: app?.invoice_details?.scanned_invoice_url },
    ].filter((d) => d.url),
  },
  {
    key: "agreement", title: "Agreement details",
    getStatus: (app) => app?.agreement_details?.status ?? null,
    getNote:   (app) => app?.agreement_details?.note ?? null,
    getFields: (app) => [
      { label: "Signatory name",     value: app?.agreement_details?.signatory_name },
      { label: "Signatory location", value: app?.agreement_details?.signatory_location },
      { label: "Date",               value: app?.agreement_details?.date },
      {
        label: "Accepted",
        value: app?.agreement_details?.accepted != null
          ? (app.agreement_details.accepted ? "Yes" : "No")
          : null,
      },
    ],
    getDocs: (app) => [
      { label: "Signature", url: app?.agreement_details?.signature_url },
    ].filter((d) => d.url),
  },
];

const REASON_CODES = [
  { value: "DOCUMENT_ILLEGIBLE",  label: "Document Illegible" },
  { value: "DOCUMENT_EXPIRED",    label: "Document Expired" },
  { value: "NAME_MISMATCH",       label: "Name Mismatch" },
  { value: "PAN_MISMATCH",        label: "PAN Mismatch" },
  { value: "GSTIN_INVALID",       label: "GSTIN Invalid" },
  { value: "BANK_PROOF_MISSING",  label: "Bank Proof Missing" },
  { value: "INVOICE_MISSING",     label: "Invoice Missing" },
  { value: "SERIAL_MISMATCH",     label: "Serial Mismatch" },
  { value: "OTHER",               label: "Other" },
];

// TODO: STAGE_CHIPS — phase 2 (advance activation stage)

const STATUS_LABELS = {
  NEW_APPLICATION:                  "New",
  UNDER_REVIEW:                     "Under Review",
  MORE_INFO_REQUIRED:               "More Info",
  ACCEPTED:                         "Accepted",
  REJECTED:                         "Rejected",
  RESOURCES_PROVISIONING_IN_PROGRESS: "Provisioning",
  RESOURCES_PROVISIONED:              "Provisioned",
  RESOURCES_PROVISIONING_FAILED:      "Provisioning Failed",
};

const statusColor = (status) => {
  switch (status) {
    case "NEW_APPLICATION":
      return { bg: "#F0F4FF", border: "#7B9EF8", text: "#2952C8" };
    case "UNDER_REVIEW":
      return { bg: "#FFF8EC", border: "#F5A623", text: "#B87200" };
    case "MORE_INFO_REQUIRED":
      return { bg: "#FFF3E0", border: "#FB8C00", text: "#E65100" };
    case "ACCEPTED":
      return { bg: "#EDF7FF", border: "#29B6F6", text: "#0277BD" };
    case "REJECTED":
      return { bg: "#FEECEC", border: "#EF5350", text: "#C62828" };
    case "RESOURCES_PROVISIONING_IN_PROGRESS":
      return { bg: "#F3EEFF", border: "#9C6FE4", text: "#6A1FD0" };
    case "RESOURCES_PROVISIONED":
      return { bg: "#EDFAF3", border: "#27AE60", text: "#1A7A42" };
    case "RESOURCES_PROVISIONING_FAILED":
      return { bg: "#FEECEC", border: "#EF5350", text: "#C62828" };
    default:
      return { bg: "#F5F5F5", border: "#BDBDBD", text: "#616161" };
  }
};

const StatusBadge = ({ status }) => {
  const { bg, border, text } = statusColor(status);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.2,
        py: 0.4,
        borderRadius: "20px",
        border: `1px solid ${border}`,
        bgcolor: bg,
      }}
    >
      <Icon icon="mdi:clock-outline" color={text} width={13} />
      <Typography variant="caption" sx={{ color: text, fontWeight: 500, whiteSpace: "nowrap" }}>
        {STATUS_LABELS[status] ?? status}
      </Typography>
    </Box>
  );
};

const AppListItem = ({ app, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 2,
      cursor: "pointer",
      border: "1px solid",
      borderColor: selected ? "primary.main" : "divider",
      bgcolor: selected ? "primary.lighter" : "background.paper",
      mb: 1,
      "&:hover": { borderColor: "primary.light", bgcolor: "action.hover" },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ letterSpacing: 0.2 }}>
        {app.application_id}
      </Typography>
      <StatusBadge status={app.status} />
    </Stack>
    <Typography variant="body2" color="text.secondary" noWrap>
      {app.business_name ?? app.phone}
    </Typography>
    <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.3}>
      <Typography variant="caption" color="text.disabled">
        {app.charger_requested?.chargerIds?.[0] ?? "-"}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {app.create_time}
      </Typography>
    </Stack>
  </Box>
);

const parseAzureBlobUrl = (url) => {
  const match = url?.match(/blob\.core\.windows\.net\/([^/?]+)\/(.+?)(\?.*)?$/);
  if (!match) return null;
  return { bucket: match[1], fileName: match[2] };
};

const fetchSasUrl = async (url, companyId) => {
  const parsed = parseAzureBlobUrl(url);
  if (!parsed) return url;
  const res = await api.get(`/cms/v1/mobile-cpo-onboarding/${companyId}/blob/get-url`, {
    params: { fileName: parsed.fileName, bucket: parsed.bucket },
  });
  return res.data?.data?.url ?? url;
};

const DocItem = ({ label, url, companyId }) => {
  const [loadingView, setLoadingView] = useState(false);

  const handleView = async () => {
    setLoadingView(true);
    try {
      const sasUrl = await fetchSasUrl(url, companyId);
      window.open(sasUrl, "_blank");
    } catch {
      window.open(url, "_blank");
    } finally {
      setLoadingView(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between"
      sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider", bgcolor: "background.neutral" }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Icon icon="mdi:file-document-outline" width={20} />
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
      </Stack>
      <Tooltip title="Preview">
        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, borderRadius: 1.5 }}
          onClick={handleView} disabled={!url || loadingView}
        >
          {loadingView ? <CircularProgress size={16} /> : <Icon icon="mdi:eye-outline" width={16} />}
        </Button>
      </Tooltip>
    </Stack>
  );
};

const sectionStatusStyle = (status) => {
  if (!status) return null;
  const s = status.toUpperCase();
  if (s === "APPROVED") return { bg: "#EDFAF3", border: "#27AE60", text: "#1A7A42" };
  if (s === "REJECTED") return { bg: "#FFF0F0", border: "#E53935", text: "#B71C1C" };
  if (s === "PENDING") return { bg: "#FFF8EC", border: "#F5A623", text: "#B87200" };
  if (s === "INFO_REQUIRED") return { bg: "#EEF2FF", border: "#5C6BC0", text: "#283593" };
  return { bg: "#F5F5F5", border: "#BDBDBD", text: "#616161" };
};

const SectionStatusChip = ({ status }) => {
  const style = sectionStatusStyle(status);
  if (!style) return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.3,
        borderRadius: "12px",
        border: `1px solid ${style.border}`,
        bgcolor: style.bg,
        ml: 1,
      }}
    >
      <Typography variant="caption" sx={{ color: style.text, fontWeight: 600, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {status}
      </Typography>
    </Box>
  );
};

const ChargerInventoryDisplay = ({ inventory = [] }) => {
  if (!inventory.length) return null;

  const field = (label, value) =>
    value ? (
      <Grid item xs={6} key={label}>
        <Typography variant="caption" color="text.disabled">{label}</Typography>
        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{value}</Typography>
      </Grid>
    ) : null;

  return (
    <Box mb={2}>
      {inventory.map((inv, idx) => {
        let connectorSummary = "-";
        if (Array.isArray(inv.connectors) && inv.connectors.length) {
          connectorSummary = inv.connectors
            .map((c, i) => `${i + 1} × ${c.connectorType ?? c.type ?? "Unknown"}`)
            .join(", ");
        }
        return (
          <Box key={inv.charge_box_id ?? idx} mb={idx < inventory.length - 1 ? 2 : 0}>
            <Typography variant="caption" color="text.disabled" fontWeight={600}
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
              {inventory.length > 1 ? `Inventory record ${idx + 1}` : "Inventory record"}
            </Typography>
            <Grid container spacing={1}>
              {field("Charger ID", inv.charge_box_id)}
              {field("Vendor", inv.vendor)}
              {field("Model", inv.model)}
              {field("Serial no", inv.serial_no)}
              {field("Charger type", inv.charger_type)}
              {field("Rated power", inv.rated_power)}
              {field("Connectors", connectorSummary)}
              {field("Installation", inv.installation)}
              {field("Rating label", inv.rating_label)}
              {field("RFID card", inv.rfid_card)}
              {field("RFID tag", inv.rfid_tag)}
              {field("Dispatch date", inv.date)}
              {field("Inventory status", inv.verification_status ?? inv.status)}
            </Grid>
            {idx < inventory.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        );
      })}
    </Box>
  );
};

const SectionFieldGrid = ({ fields = [] }) => {
  const visible = fields.filter((f) => f.value != null && f.value !== "");
  if (!visible.length) return null;
  return (
    <Box mb={2}>
      <Grid container spacing={1}>
        {visible.map(({ label, value }) => (
          <Grid item xs={6} key={label}>
            <Typography variant="caption" color="text.disabled">{label}</Typography>
            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-all" }}>{value}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const SectionReviewCard = ({ title, status, note, docs = [], sectionKey, requestPk, children }) => {
  const { teamId: companyId } = useSelectedTeam();
  const queryClient = useQueryClient();
  const [noteMode, setNoteMode] = useState(false);
  const [noteValue, setNoteValue] = useState("");

  const { mutate, isLoading } = useMutation(fetchApi, {
    onSuccess: (response, variables) => {
      const newSectionStatus = variables.body.status;
      const column = SECTION_TO_COLUMN[sectionKey];

      setNoteMode(false);
      setNoteValue("");

      // update detail cache — patch section status + note + main status in place
      queryClient.setQueryData(
        queryKeys.mobileCpoOnboardingDetail(companyId, requestPk),
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              status: response.mainStatus ?? old.data.status,
              [column]: {
                ...(old.data[column] ?? {}),
                status: newSectionStatus,
                note: variables.body.note ?? null,
              },
            },
          };
        }
      );

      // update list cache — patch only the main status of the matching row
      queryClient.setQueriesData(["mobileCpoOnboardingList"], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((item) =>
            item.request_pk === requestPk
              ? { ...item, status: response.mainStatus ?? item.status }
              : item
          ),
        };
      });

      // refetch timeline independently
      queryClient.invalidateQueries(queryKeys.mobileCpoOnboardingTimeline(companyId, requestPk));
    },
  });

  const handleAction = (sectionStatus, sectionNote) => {
    mutate({
      url: mobileCpoOnboardingSectionUpdateUrl(companyId, requestPk, sectionKey),
      method: "PATCH",
      body: { status: sectionStatus, ...(sectionNote ? { note: sectionNote } : {}) },
    });
  };

  const handleRequestInfo = () => {
    setNoteValue(note ?? "");
    setNoteMode(true);
  };

  const handleCancelNote = () => {
    setNoteMode(false);
    setNoteValue("");
  };

  return (
    <Accordion variant="outlined" disableGutters
      sx={{ borderRadius: "8px !important", "&:before": { display: "none" }, overflow: "hidden" }}
    >
      <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" width={20} />} sx={{ "& .MuiAccordionSummary-content": { width: "100%", mr: 1 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
          <SectionStatusChip status={status} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {children}

        {docs.length > 0 ? (
          <Stack spacing={1} mb={2}>
            {docs.map((doc) => (
              <DocItem key={doc.label} label={doc.label} url={doc.url} companyId={companyId} />
            ))}
          </Stack>
        ) : (
          !children && (
            <Typography variant="caption" color="text.disabled" display="block" mb={2}>
              No documents for this section
            </Typography>
          )
        )}

        {(note || noteMode) && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Note"
            value={noteMode ? noteValue : (note ?? "")}
            onChange={noteMode ? (e) => setNoteValue(e.target.value) : undefined}
            InputProps={{ readOnly: !noteMode }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                bgcolor: noteMode ? "background.paper" : "background.neutral",
              },
            }}
          />
        )}

        {noteMode ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small" variant="contained" color="warning"
              sx={{ borderRadius: 5, textTransform: "none" }}
              disabled={isLoading || !noteValue.trim()}
              onClick={() => handleAction("INFO_REQUIRED", noteValue.trim())}
            >
              Submit request
            </Button>
            <Button
              size="small" variant="outlined"
              sx={{ borderRadius: 5, textTransform: "none" }}
              disabled={isLoading}
              onClick={handleCancelNote}
            >
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Button
              size="small" variant="outlined" color="success"
              sx={{ borderRadius: 5, textTransform: "none" }}
              disabled={isLoading || status === "APPROVED"}
              onClick={() => handleAction("APPROVED")}
            >
              Approve section
            </Button>
            <Button
              size="small" variant="outlined" color="error"
              sx={{ borderRadius: 5, textTransform: "none" }}
              disabled={isLoading || status === "REJECTED"}
              onClick={() => handleAction("REJECTED")}
            >
              Reject section
            </Button>
            <Button
              size="small"
              sx={{ textTransform: "none", color: "text.secondary" }}
              disabled={isLoading || status === "INFO_REQUIRED"}
              onClick={handleRequestInfo}
            >
              Request information
            </Button>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const DetailPanel = ({ app, loading }) => {
  const { teamId: companyId } = useSelectedTeam();
  const queryClient = useQueryClient();

  const [action, setAction] = useState(null); // "approve" | "reject"
  const [reasonCode, setReasonCode] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const [provisionResources, setProvisionResources] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffTo, setHandoffTo] = useState("");
  const [handoffCc, setHandoffCc] = useState("");
  const [handoffNote, setHandoffNote] = useState("");
  const [handoffWsUrl, setHandoffWsUrl] = useState("");

  const { data: timelineResponse, isFetching: isTimelineFetching } = useQuery(
    queryKeys.mobileCpoOnboardingTimeline(companyId, app?.request_pk),
    fetchWithError,
    { enabled: !!companyId && !!app?.request_pk }
  );
  const timeline = timelineResponse?.data ?? [];

  const { data: handoffPreviewResponse, isFetching: isPreviewFetching } = useQuery(
    queryKeys.mobileCpoOnboardingHandoffPreview(companyId, app?.request_pk),
    fetchWithError,
    { enabled: !!companyId && !!app?.request_pk && handoffOpen, staleTime: 0 }
  );
  const previewData = handoffPreviewResponse?.data ?? null;

  const handleActionClick = (type) => {
    setAction(type);
    setReasonCode("");
    setInternalNote("");
    setCustomerNote("");
    setProvisionResources(false);
  };

  const handleCancel = () => {
    setAction(null);
    setReasonCode("");
    setInternalNote("");
    setCustomerNote("");
    setProvisionResources(false);
  };

  const { mutate: updateStatus, isLoading: isStatusUpdating } = useMutation(fetchApi, {
    onSuccess: (response, variables) => {
      const newStatus = response.status;
      // provision-only call has exactly one key; skip overwriting notes in that case
      const isProvisionOnly = newStatus === "RESOURCES_PROVISIONING_IN_PROGRESS" && Object.keys(variables.body).length === 1;

      queryClient.setQueryData(
        queryKeys.mobileCpoOnboardingDetail(companyId, app.request_pk),
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              status: newStatus,
              ...(!isProvisionOnly && {
                reason_code: reasonCode || null,
                internal_note: internalNote || null,
                customer_note: customerNote || null,
              }),
            },
          };
        }
      );

      queryClient.setQueriesData(["mobileCpoOnboardingList"], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((item) =>
            item.request_pk === app.request_pk
              ? { ...item, status: newStatus }
              : item
          ),
        };
      });

      queryClient.invalidateQueries(queryKeys.mobileCpoOnboardingTimeline(companyId, app.request_pk));
      queryClient.invalidateQueries(["mobileCpoOnboardingStats"]);

      handleCancel();
    },
  });

  const handleConfirm = () => {
    updateStatus({
      url: mobileCpoOnboardingStatusUpdateUrl(companyId, app.request_pk),
      method: "PATCH",
      body: {
        status: action === "approve"
          ? (provisionResources ? "RESOURCES_PROVISIONING_IN_PROGRESS" : "ACCEPTED")
          : "REJECTED",
        ...(action === "reject" && { reason_code: reasonCode }),
        ...(internalNote.trim() && { internal_note: internalNote.trim() }),
        ...(customerNote.trim() && { customer_note: customerNote.trim() }),
      },
    });
  };

  const handleProvision = () => {
    updateStatus({
      url: mobileCpoOnboardingStatusUpdateUrl(companyId, app.request_pk),
      method: "PATCH",
      body: { status: "RESOURCES_PROVISIONING_IN_PROGRESS" },
    });
  };

  const { mutate: sendHandoff, isLoading: isSendingHandoff } = useMutation(fetchApi, {
    onSuccess: () => {
      setHandoffOpen(false);
      setHandoffTo("");
      setHandoffCc("");
      setHandoffNote("");
      setHandoffWsUrl("");
      queryClient.invalidateQueries(queryKeys.mobileCpoOnboardingTimeline(companyId, app.request_pk));
    },
  });

  const handleOpenHandoff = () => {
    const wsBase = process.env.REACT_APP_WEBSOCKET_ENDPOINT ?? "";
    setHandoffWsUrl(wsBase ? `wss://${wsBase}` : "");
    setHandoffOpen(true);
  };

  const handleSendHandoff = () => {
    const toList = handoffTo.split(",").map((e) => e.trim()).filter(Boolean);
    const ccList = handoffCc.split(",").map((e) => e.trim()).filter(Boolean);
    sendHandoff({
      url: mobileCpoOnboardingHandoffEmailUrl(companyId, app.request_pk),
      method: "POST",
      body: {
        to: toList,
        ...(ccList.length > 0 && { cc: ccList }),
        wsUrl: handoffWsUrl,
        ...(handoffNote.trim() && { note: handoffNote.trim() }),
      },
    });
  };

  const buildEmailPreview = () => {
    if (!app) return "";
    const bd = app.business_details ?? {};
    const gd = app.gstin_details ?? {};
    const cr = app.charger_requested ?? {};
    const addr = cr.address ?? {};

    const chargerList = previewData?.chargers ?? [];

    const team = previewData?.team ?? null;
    const cpoName   = team?.teamName   ?? bd.business_name ?? bd.name ?? "Not assigned";
    const tradeName = team?.operator_name ?? bd.trade_name ?? bd.business_name ?? bd.name ?? "Not assigned";

    const col = (label, value) => `   ${label.padEnd(22)}: ${value ?? "-"}`;
    let sectionNum = 1;
    const lines = [
      "Hello OEM Field Services team,",
      "",
      "Please schedule a site visit and complete the on-site configuration for the charge point(s) below.",
      "",
    ];

    if (chargerList.length > 0) {
      chargerList.forEach((inv, idx) => {
        let connectorSummary = "-";
        if (inv?.connectors) {
          try {
            const parsed = typeof inv.connectors === "string" ? JSON.parse(inv.connectors) : inv.connectors;
            if (Array.isArray(parsed) && parsed.length > 0) {
              connectorSummary = parsed
                .map((c, i) => `${i + 1} × ${c.connectorType ?? c.type ?? "Unknown"}`)
                .join(", ");
            }
          } catch (_) { /* leave as "-" */ }
        }
        lines.push(
          `${sectionNum++}. CHARGER${chargerList.length > 1 ? ` ${idx + 1}` : ""} DETAILS`,
          col("Charger / Serial ID", inv?.charge_box_id ?? "-"),
          col("OCPP identity", inv?.charge_box_id ?? "-"),
          col("Make / Model", [inv?.vendor, inv?.model].filter(Boolean).join(" ") || "-"),
          col("Serial no", inv?.serial_no ?? "-"),
          col("Rated power", inv?.rated_power ?? "-"),
          col("Charger type", inv?.charger_type ?? "-"),
          col("Connectors", connectorSummary),
          col("Installation", inv?.installation ?? "-"),
          col("Rating label", inv?.rating_label ?? "-"),
          col("RFID card", inv?.rfid_card ?? "-"),
          col("RFID tag", inv?.rfid_tag ?? "-"),
          col("Dispatch date", inv?.date ?? "-"),
          col("Field status", inv?.verification_status ?? inv?.status ?? "-"),
          "",
        );
      });
    } else {
      const chargerIdList = (cr.chargerIds ?? []).join(", ") || "-";
      lines.push(
        `${sectionNum++}. CHARGER DETAILS`,
        col("Charger / Serial ID", chargerIdList),
        col("OCPP identity", chargerIdList),
        col("Make / Model", "-"),
        col("Serial no", "-"),
        col("Rated power", "-"),
        col("Charger type", "-"),
        col("Connectors", "-"),
        col("Installation", "-"),
        col("Rating label", "-"),
        col("RFID card", "-"),
        col("RFID tag", "-"),
        col("Dispatch date", "-"),
        col("Field status", "-"),
        "",
      );
    }

    lines.push(
      `${sectionNum++}. CPO DETAILS`,
      col("CPO / Legal name", cpoName),
      col("Trading name", tradeName),
      col("Entity type", bd.business_type ?? "-"),
      col("Mobile", app.phone ?? "-"),
      col("Alternate mobile", app.alternate_phone ?? "-"),
      col("Email", app.email ?? "-"),
      col("GSTIN", gd.gstin ?? "-"),
      col("Team ID", previewData?.teamId ? String(previewData.teamId) : "Not assigned"),
      "",
    );

    if (addr.city || addr.address_line1) {
      lines.push(`${sectionNum++}. SITE ADDRESS (installation)`);
      if (addr.address_line1) lines.push(`   ${addr.address_line1}`);
      if (addr.address_line2) lines.push(`   ${addr.address_line2}`);
      const cityState = [addr.city, addr.state].filter(Boolean).join(", ");
      if (cityState) lines.push(`   ${cityState}`);
      if (addr.zip_code) lines.push(`   PIN - ${addr.zip_code}`);
      lines.push("");
    }

    const wsBase = handoffWsUrl
      ? (handoffWsUrl.endsWith("/") ? handoffWsUrl : `${handoffWsUrl}/`)
      : "";
    if (chargerList.length > 0) {
      chargerList.forEach((inv, idx) => {
        const chargerWsUrl = wsBase && inv?.charge_box_id
          ? `${wsBase}${inv.charge_box_id}`
          : handoffWsUrl || "-";
        lines.push(
          `${sectionNum++}. ZYNKA CPMS CONNECTIVITY${chargerList.length > 1 ? ` ${idx + 1}` : ""}`,
          col("Protocol", "OCPP 1.6J (JSON over WebSocket)"),
          col("WebSocket URL", chargerWsUrl),
          col("Charge point ID", inv?.charge_box_id ?? "-"),
          col("Auth", "Provisioning token issued by Zynka ops"),
          "",
        );
      });
    } else {
      lines.push(
        `${sectionNum++}. ZYNKA CPMS CONNECTIVITY`,
        col("Protocol", "OCPP 1.6J (JSON over WebSocket)"),
        col("WebSocket URL", handoffWsUrl || "-"),
        col("Charge point ID", cr.chargerIds?.[0] ?? "-"),
        col("Auth", "Provisioning token issued by Zynka ops"),
        "",
      );
    }

    lines.push(
      `${sectionNum++}. APPLICATION`,
      col("Reference", app.application_id ?? "-"),
      col("Current stage", STATUS_LABELS[app.status] ?? app.status ?? "-"),
      "",
    );

    if (handoffNote.trim()) {
      lines.push("NOTE TO OEM TEAM", handoffNote.trim(), "");
    }

    lines.push(
      "Regards,",
      "Zynka CMS — Mobile CPO Onboarding",
      "",
      "Support: zynkasupport@zynka.ai",
      "Phone: +91 94296 93773",
      "WhatsApp: +91 87200 10087",
    );
    return lines.join("\n");
  };

  const handoffSubject = app
    ? `Field visit & configuration request — ${(app.charger_requested?.chargerIds ?? []).join(", ") || "-"} · ${app.application_id ?? "-"}`
    : "";

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <CircularProgress size={32} />
      </Card>
    );
  }

  if (!app) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <Stack alignItems="center" spacing={1} color="text.disabled">
          <Icon icon="mdi:email-outline" width={48} />
          <Typography variant="body1">Select an application from the queue</Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, overflow: "auto" }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Typography variant="h6" fontWeight={700}>{app.application_id}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon icon="mdi:email-send-outline" width={16} />}
              sx={{ borderRadius: 5, textTransform: "none", fontSize: "0.75rem" }}
              onClick={handleOpenHandoff}
            >
              Send handoff to OEM
            </Button>
            <StatusBadge status={app.status} />
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {app.business_details?.name ?? "-"} &middot; {app.phone} {app.email ? `· ${app.email}` : ""}
        </Typography>

        <Grid container spacing={2} mb={2}>
          {[
            { label: "Phone", value: app.phone ?? "-" },
            { label: "Email", value: app.email ?? "-" },
            { label: "Charger ID", value: app.charger_requested?.chargerIds?.[0] ?? "-" },
            { label: "Entity type", value: app.business_details?.gstin ? "GST Registered" : "-" },
            { label: "PAN", value: app.kyc_details?.pan ?? "-" },
            { label: "Bank account", value: app.bank_details?.account_no ?? "-" },
            { label: "IFSC", value: app.bank_details?.ifsc_code ?? "-" },
            { label: "Submitted", value: app.create_time },
          ].map(({ label, value }) => (
            <Grid item xs={6} key={label}>
              <Typography variant="caption" color="text.disabled">{label}</Typography>
              <Typography variant="body2" fontWeight={500}>{value}</Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* TODO: Assign to me — phase 2 */}
        {/* <Button variant="outlined" size="small" sx={{ borderRadius: 5, textTransform: "none", mb: 2 }}>
          Assign to me
        </Button> */}

        <Stack spacing={1.5} mb={2}>
          {REVIEW_SECTIONS.map((section) => (
            <SectionReviewCard
              key={section.key}
              sectionKey={section.key}
              requestPk={app.request_pk}
              title={section.title}
              status={section.getStatus(app)}
              note={section.getNote(app)}
              docs={section.getDocs(app)}
            >
              {section.key === "charger" ? (
                <>
                  <SectionFieldGrid fields={section.getFields(app)} />
                  <ChargerInventoryDisplay inventory={app.charger_inventory ?? []} />
                </>
              ) : (
                <SectionFieldGrid fields={section.getFields(app)} />
              )}
            </SectionReviewCard>
          ))}
        </Stack>

        {app.status !== "ACCEPTED" && app.status !== "REJECTED" && app.status !== "RESOURCES_PROVISIONED" && app.status !== "RESOURCES_PROVISIONING_IN_PROGRESS" && app.status !== "RESOURCES_PROVISIONING_FAILED" && (
          <>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1} mb={2}>
              <Tooltip
                title={
                  REVIEW_SECTIONS.every((s) => s.getStatus(app) === "APPROVED")
                    ? ""
                    : "All sections must be approved before accepting"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 5, textTransform: "none" }}
                    disabled={
                      isStatusUpdating ||
                      !REVIEW_SECTIONS.every((s) => s.getStatus(app) === "APPROVED")
                    }
                    onClick={() => handleActionClick("approve")}
                  >
                    Approve for provisioning
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                sx={{ borderRadius: 5, textTransform: "none" }}
                disabled={isStatusUpdating}
                onClick={() => handleActionClick("reject")}
              >
                Reject application
              </Button>
            </Stack>

            {action && (
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                  {action === "approve" ? "Approve for provisioning" : "Reject application"}
                </Typography>

                {action === "approve" && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={provisionResources}
                        onChange={(e) => setProvisionResources(e.target.checked)}
                        disabled={isStatusUpdating}
                      />
                    }
                    label="Provision the required resources"
                    sx={{ mb: 1.5, display: "block" }}
                  />
                )}

                {action === "reject" && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }} required>
                    <InputLabel>Reason code *</InputLabel>
                    <Select
                      label="Reason code *"
                      value={reasonCode}
                      onChange={(e) => setReasonCode(e.target.value)}
                    >
                      {REASON_CODES.map((rc) => (
                        <MenuItem key={rc.value} value={rc.value}>{rc.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Internal note"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Customer-visible note"
                  value={customerNote}
                  onChange={(e) => setCustomerNote(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    color={action === "reject" ? "error" : "primary"}
                    sx={{ borderRadius: 5, textTransform: "none" }}
                    disabled={isStatusUpdating || (action === "reject" && !reasonCode)}
                    onClick={handleConfirm}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: 5, textTransform: "none" }}
                    disabled={isStatusUpdating}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Card>
            )}
          </>
        )}

        {["ACCEPTED", "REJECTED", "RESOURCES_PROVISIONING_IN_PROGRESS", "RESOURCES_PROVISIONED", "RESOURCES_PROVISIONING_FAILED"].includes(app.status) &&
          (app.reason_code || app.internal_note || app.customer_note) && (
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: "background.neutral" }}>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Decision record</Typography>
            {app.reason_code && (
              <TextField
                fullWidth
                size="small"
                label="Reason code"
                value={REASON_CODES.find((rc) => rc.value === app.reason_code)?.label ?? app.reason_code}
                InputProps={{ readOnly: true }}
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
              />
            )}
            {app.internal_note && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Internal note"
                value={app.internal_note}
                InputProps={{ readOnly: true }}
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
              />
            )}
            {app.customer_note && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Customer-visible note"
                value={app.customer_note}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
              />
            )}
          </Card>
        )}

        {(app.status === "ACCEPTED" || app.status === "RESOURCES_PROVISIONING_FAILED") && (
          <Box mb={2}>
            <Button
              variant="contained"
              color={app.status === "RESOURCES_PROVISIONING_FAILED" ? "error" : "success"}
              sx={{ borderRadius: 5, textTransform: "none" }}
              disabled={isStatusUpdating}
              onClick={handleProvision}
            >
              {app.status === "RESOURCES_PROVISIONING_FAILED" ? "Retry provisioning" : "Provision resources"}
            </Button>
          </Box>
        )}

        {/* TODO: Advance activation stage — phase 2 */}
        {/* <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.neutral", mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
            Advance activation stage
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Move the application to the next stage: provisioning, field configuration, connection, payment QR, test session and payouts.
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" mb={1.5}>
            Current stage: UNDER REVIEW
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {STAGE_CHIPS.map((chip) => (
              <Chip
                key={chip}
                label={chip}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 1, fontSize: "0.7rem", cursor: "pointer" }}
              />
            ))}
          </Stack>
        </Card> */}

        <Typography variant="subtitle2" fontWeight={700} mb={2}>
          Activity log
        </Typography>
        {isTimelineFetching ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={20} />
          </Stack>
        ) : timeline.length === 0 ? (
          <Typography variant="body2" color="text.disabled">No activity yet</Typography>
        ) : (
          <Stack spacing={0}>
            {timeline.map((entry, i, arr) => {
              const statusMeta = TIMELINE_STATUS_COLORS[entry.status] ?? { color: "default", dotColor: "#9e9e9e" };
              const prevMeta   = TIMELINE_STATUS_COLORS[entry.prevStatus] ?? { color: "default", dotColor: "#9e9e9e" };
              return (
                <Box key={entry.timelinePk ?? i} sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.5 }}>
                    <Box sx={{
                      width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                      bgcolor: statusMeta.dotColor,
                      boxShadow: `0 0 0 3px ${statusMeta.dotColor}33`,
                    }} />
                    {i < arr.length - 1 && (
                      <Box sx={{ width: "2px", flex: 1, bgcolor: "divider", minHeight: 20, my: 0.75 }} />
                    )}
                  </Box>
                  <Box pb={2.5}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" mb={0.5}>
                      {entry.prevStatus && (
                        <>
                          <Chip
                            label={TIMELINE_STATUS_LABELS[entry.prevStatus] ?? entry.prevStatus}
                            size="small"
                            color={prevMeta.color}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 22 }}
                          />
                          <Icon icon="mdi:arrow-right" width={16} color="#9e9e9e" />
                        </>
                      )}
                      <Chip
                        label={TIMELINE_STATUS_LABELS[entry.status] ?? entry.status}
                        size="small"
                        color={statusMeta.color}
                        variant="filled"
                        sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
                      />
                    </Stack>
                    {entry.description && (
                      <Typography variant="body2" color="text.primary" fontWeight={500} mb={0.25}>
                        {entry.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {entry.createTime ? new Date(entry.createTime).toLocaleString() : ""}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* OEM field-services handoff dialog */}
      <Dialog open={handoffOpen} onClose={() => setHandoffOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700}>OEM field-services handoff</Typography>
              <Typography variant="body2" color="text.secondary">
                Review the brief below, then send it to the OEM field-services team.
              </Typography>
            </Box>
            <IconButton onClick={() => setHandoffOpen(false)} size="small">
              <Icon icon="mdi:close" width={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="To *"
            placeholder="fieldservices@oem.example"
            helperText="Comma-separated. Use the vetted OEM field-services distribution list."
            value={handoffTo}
            onChange={(e) => setHandoffTo(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Cc"
            placeholder="manager@company.com"
            value={handoffCc}
            onChange={(e) => setHandoffCc(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Zynka CPMS WebSocket URL (base) *"
            helperText="Base path — each charger's ID is appended automatically. Override for a different environment."
            value={handoffWsUrl}
            onChange={(e) => setHandoffWsUrl(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Note to OEM team"
            placeholder="Any special instructions, access codes, or site-specific notes..."
            inputProps={{ maxLength: 1000 }}
            helperText={`${handoffNote.length}/1000`}
            value={handoffNote}
            onChange={(e) => setHandoffNote(e.target.value)}
          />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                Email preview
              </Typography>
              {isPreviewFetching && <CircularProgress size={12} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {handoffSubject}
            </Typography>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.78rem",
                bgcolor: "background.neutral",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                p: 2,
                whiteSpace: "pre-wrap",
                overflowX: "auto",
                maxHeight: 340,
                overflowY: "auto",
                lineHeight: 1.7,
              }}
            >
              {buildEmailPreview()}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            sx={{ borderRadius: 5, textTransform: "none" }}
            onClick={() => setHandoffOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isSendingHandoff ? <CircularProgress size={14} color="inherit" /> : <Icon icon="mdi:send-outline" width={16} />}
            sx={{ borderRadius: 5, textTransform: "none" }}
            disabled={isSendingHandoff || !handoffTo.trim() || !handoffWsUrl.trim()}
            onClick={handleSendHandoff}
          >
            Send handoff to OEM
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const VerificationQueueTab = () => {
  const { teamId: companyId } = useSelectedTeam();
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [selectedId, setSelectedId] = useState(null);

  const { data: response, isFetching } = useQuery(
    queryKeys.mobileCpoOnboardingList(companyId, page, pageSize, searchValue, statusFilter),
    fetchWithError,
    { enabled: !!companyId, keepPreviousData: true }
  );

  const { data: detailResponse, isFetching: isDetailFetching } = useQuery(
    queryKeys.mobileCpoOnboardingDetail(companyId, selectedId),
    fetchWithError,
    { enabled: !!companyId && !!selectedId }
  );

  const applications = response?.data ?? [];
  const totalRecords = response?.totalRecord ?? 0;
  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const selectedApp = detailResponse?.data ?? null;

  const debouncedApplySearch = useRef(
    debounceFunc((val) => {
      setPage(0);
      setSearchValue(val);
    }, 600)
  ).current;

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    debouncedApplySearch(val);
  };

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Card
        variant="outlined"
        sx={{
          width: 360,
          flexShrink: 0,
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 600,
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, phone, or application ID"
            value={search}
            onChange={handleSearch}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="eva:search-fill" width={18} />
                </InputAdornment>
              ),
            }}
          />
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
          {isFetching ? (
            <Stack alignItems="center" justifyContent="center" height="100%" py={4}>
              <CircularProgress size={24} />
            </Stack>
          ) : applications.length === 0 ? (
            <Typography variant="body2" color="text.disabled" textAlign="center" mt={4}>
              No applications found
            </Typography>
          ) : (
            applications.map((app) => (
              <AppListItem
                key={app.request_pk}
                app={app}
                selected={app.request_pk === selectedId}
                onClick={() => setSelectedId(app.request_pk)}
              />
            ))
          )}
        </Box>
        {totalPages > 1 && (
          <Box sx={{ p: 1, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, newPage) => setPage(newPage - 1)}
              size="small"
              siblingCount={0}
            />
          </Box>
        )}
      </Card>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <DetailPanel key={selectedId} app={selectedApp} loading={isDetailFetching} />
      </Box>
    </Stack>
  );
};

export default VerificationQueueTab;
